import type { Message } from '@/types/models'

/** Max time between consecutive bare image messages to merge into one burst (WhatsApp-style). */
export const CHAT_IMAGE_BURST_MAX_GAP_MS = 3 * 60 * 1000

export function getMessageTimestampAsDate(message: Message): Date {
  const timestampValue = message.timestamp as unknown
  if (
    timestampValue !== null &&
    typeof timestampValue === 'object' &&
    'toDate' in timestampValue &&
    typeof (timestampValue as { toDate: unknown }).toDate === 'function'
  ) {
    return (timestampValue as { toDate: () => Date }).toDate()
  }
  if (timestampValue instanceof Date) return timestampValue
  return new Date(timestampValue as string | number)
}

export function getFlatImageUrlsFromMessage(message: Message): string[] {
  if (message.type !== 'image') return []
  const urls: string[] = []
  if (message.imageUrl) urls.push(message.imageUrl)
  if (message.imageUrls?.length) urls.push(...message.imageUrls)
  return urls
}

/** Image-only message (no caption) — may join a multi-message image burst. */
export function isBareImageBurstPartMessage(message: Message): boolean {
  return (
    message.type === 'image' &&
    getFlatImageUrlsFromMessage(message).length > 0 &&
    !(message.content || '').trim()
  )
}

export function canAppendMessageToImageBurst(previousMessage: Message, nextMessage: Message): boolean {
  if (!isBareImageBurstPartMessage(nextMessage)) return false
  if (
    previousMessage.senderId !== nextMessage.senderId ||
    previousMessage.senderType !== nextMessage.senderType
  ) {
    return false
  }
  const previousTime = getMessageTimestampAsDate(previousMessage).getTime()
  const nextTime = getMessageTimestampAsDate(nextMessage).getTime()
  return nextTime - previousTime <= CHAT_IMAGE_BURST_MAX_GAP_MS
}

export function getMessageBubbleCornerClassName(
  isContinuation: boolean,
  isOwnMessage: boolean
): string {
  return isContinuation
    ? isOwnMessage
      ? 'rounded-[18px] rounded-tr-[2px] rounded-br-[2px]'
      : 'rounded-[18px] rounded-tl-[2px] rounded-bl-[2px]'
    : isOwnMessage
      ? 'rounded-[18px] rounded-br-[2px]'
      : 'rounded-[18px] rounded-bl-[2px]'
}

/**
 * Cite strip top corners: inner radius ≈ outer bubble top radius − lateral inset
 * (concentric rounding: innerR = outerR − padding). Lateral inset matches cite wrapper
 * `paddingInline: calc(var(--spacing) * 2)`.
 */
export function getInlineReplyQuoteCornerClassName(
  isContinuation: boolean,
  isOwnMessage: boolean
): string {
  if (isContinuation) {
    if (isOwnMessage) {
      return 'rounded-tl-[max(0px,calc(18px_-_var(--spacing)_*_2))] rounded-tr-[max(0px,calc(2px_-_var(--spacing)_*_2))] rounded-b-[6px]'
    }
    return 'rounded-tl-[max(0px,calc(2px_-_var(--spacing)_*_2))] rounded-tr-[max(0px,calc(18px_-_var(--spacing)_*_2))] rounded-b-[6px]'
  }
  return 'rounded-tl-[max(0px,calc(18px_-_var(--spacing)_*_2))] rounded-tr-[max(0px,calc(18px_-_var(--spacing)_*_2))] rounded-b-[6px]'
}
